<template>
  <div class="footer-nav-area" id="footerNav">
    <nav class="bottom-nav">
      <!-- Market -->
      <div class="nav-item" :class="{ active: currentRoute === localePath('/') }">
        <NuxtLink :to="localePath('/')">
          <div class="nav-icon">
            <i class="fa-solid fa-house"></i>
          </div>
          <span class="nav-label">{{ $t('bottomNav.market') }}</span>
        </NuxtLink>
      </div>

      <!-- Support -->
      <div class="nav-item" :class="{ active: currentRoute === localePath('/products') }">
        <NuxtLink :to="localePath('/products')">
          <div class="nav-icon">
            <i class="fa-solid fa-comment-dots"></i>
          </div>
          <span class="nav-label">{{ $t('bottomNav.products') }}</span>
        </NuxtLink>
      </div>

      <!-- Register (center floating button) -->
      <div class="nav-item center-btn" @click="openModal">
        <div class="center-circle">
          <i class="fa-solid fa-plus"></i>
        </div>
        <span class="nav-label">{{ $t('bottomNav.register') }}</span>
      </div>

      <!-- My Sarvban -->
      <div class="nav-item" :class="{ active: currentRoute.includes(localePath('/my-sarvban'),localePath('/my-sarvban/orders'),localePath('/my-adv')) }">
        <NuxtLink :to="localePath('/my-sarvban')">
          <div class="nav-icon">
            <i class="fa-solid fa-gear"></i>
          </div>
          <span class="nav-label">{{ $t('bottomNav.my_sarvban') }}</span>
        </NuxtLink>
      </div>

      <!-- Contact -->
      <div class="nav-item" :class="{ active: currentRoute === localePath('/contact') }">
        <NuxtLink :to="localePath('/contact')">
          <div class="nav-icon">
            <i class="fa-solid fa-heart"></i>
          </div>
          <span class="nav-label">{{ $t('bottomNav.contact') }}</span>
        </NuxtLink>
      </div>
    </nav>

<!-- Modal Overlay -->
    <Teleport to="body">
      <div v-if="showModal">
        <motion.div
          class="modal-overlay"
          @click.self="closeModal"
          :initial="{ opacity: 0 }"
          :animate="{ opacity: 1 }"
          :exit="{ opacity: 0 }"
          :transition="{ duration: 0.25 }"
        >
          <motion.div
            class="modal-content"
            @click.stop
            :initial="{ opacity: 0, y: 20 }"
            :animate="{ opacity: 1, y: 0 }"
            :exit="{ opacity: 0, y: 20 }"
            :transition="{ type: 'spring', stiffness: 300, damping: 30 }"
          >
            <div class="modal-header">
              <h3>{{ $t('bottomNav.registerTitle') }}</h3>
              <motion.button
                class="modal-close"
                @click="closeModal"
                aria-label="Close"
                :whileHover="{ scale: 1.1 }"
                :whilePress="{ scale: 0.9 }"
                :transition="{ type: 'spring', stiffness: 400 }"
              >
                <i class="fa-solid fa-xmark"></i>
              </motion.button>
            </div>
            <div class="modal-options">
              <motion.button
                class="modal-option"
                @click="navigateTo('/add-adv')"
                :whileHover="{ x: -4 }"
                :whilePress="{ scale: 0.98 }"
                :transition="{ type: 'spring', stiffness: 300 }"
              >
                <div class="option-icon farmer">
                  <i class="fa-solid fa-seedling"></i>
                </div>
                <div class="option-text">
                  <span class="option-title">{{ $t('bottomNav.farmer') }}</span>
                  <span class="option-desc">{{ $t('bottomNav.farmerDesc') }}</span>
                </div>
                <i class="fa-solid fa-chevron-left option-arrow"></i>
              </motion.button>
              <motion.button
                class="modal-option"
                @click="navigateTo('/add-ord')"
                :whileHover="{ x: -4 }"
                :whilePress="{ scale: 0.98 }"
                :transition="{ type: 'spring', stiffness: 300 }"
              >
                <div class="option-icon buyer">
                  <i class="fa-solid fa-user"></i>
                </div>
                <div class="option-text">
                  <span class="option-title">{{ $t('bottomNav.buyer') }}</span>
                  <span class="option-desc">{{ $t('bottomNav.buyerDesc') }}</span>
                </div>
                <i class="fa-solid fa-chevron-left option-arrow"></i>
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter, useLocalePath } from '#imports'

const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()

const currentRoute = computed(() => route.path)
const showModal = ref(false)

const openModal = () => {
  showModal.value = true
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  showModal.value = false
  document.body.style.overflow = ''
}

const navigateTo = (path: string) => {
  closeModal()
  router.push(localePath(path))
}
</script>

<style lang="scss" scoped>
// ─── Design Tokens ───
$green-glow: rgba(46, 204, 113, 0.35);
$green-tint: rgba(46, 204, 113, 0.1);
 $nav-bg: #FFFFFF;
 $nav-inactive: #8E8E93;
 $nav-active:$green;

// ─── Container ───
.footer-nav-area {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
}

// ─── Nav Bar ───
.bottom-nav {
  height: 82px;
  background: $nav-bg;
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  padding-top: 8px;
  border-top: 0.5px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.05);
}

// ─── Nav Item ───
.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  position: relative;
  -webkit-user-select: none;
  user-select: none;

  a {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-decoration: none;
    width: 100%;
    transition: transform 0.15s ease;

    &:active {
      transform: scale(0.88);
    }
  }

  .nav-icon {
    width: 44px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    transition: background 0.25s ease;

    i {
      font-size: 20px;
      color: $nav-inactive;
      transition: color 0.25s ease;
    }
  }

  .nav-label {
    font-size: 10px;
    font-weight: 500;
    color: $nav-inactive;
    transition: color 0.25s ease, font-weight 0.25s ease;
    letter-spacing: 0.1px;
  }

  // ─── Active State ───
  &.active {
    .nav-icon {
      background:$green-tint;

      i {
        color: $nav-active;
      }
    }

    .nav-label {
      color: $nav-active;
      font-weight: 600;
    }
  }

  // ─── Center Floating Button ───
  &.center-btn {
    margin-top: -28px;

    a {
      gap: 4px;
    }

    .center-circle {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: linear-gradient(180deg,$green,$green-dark);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow:
        0 4px 18px$green-glow,
        0 0 0 4px rgba(46, 204, 113, 0.1);
      transition: all 0.25s ease;

      i {
        color: #fff;
        font-size: 22px;
      }
    }

    a:active .center-circle {
      transform: scale(0.9);
      box-shadow:
        0 2px 8px$green-glow,
        0 0 0 3px rgba(46, 204, 113, 0.08);
    }

    .nav-label {
      margin-top: 4px;
    }

    // Override default active styling for center button
    &.active .center-circle {
      box-shadow:
        0 4px 18px$green-glow,
        0 0 0 5px rgba(46, 204, 113, 0.15);
    }
  }
}

// ─── Body spacing ───
// :global(body) {
//   padding-bottom: 86px;
// }

// ─── Modal ───
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: $nav-bg;
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-width: 480px;
  padding: 24px 20px 32px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1a1a2e;
    letter-spacing: -0.02em;
  }
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: none;
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8E8E93;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: #1a1a2e;
  }

  i {
    font-size: 16px;
  }
}

.modal-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: right;
  direction: rtl;

  &:hover {
    border-color:$green;
    background:$green-tint;
    transform: translateX(-4px);
  }

  &:active {
    transform: translateX(0) scale(0.98);
  }
}

.option-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 22px;
    color: #fff;
  }

  &.farmer {
    background: linear-gradient(135deg, #2ECC71, #58D68D);
  }

  &.buyer {
    background: linear-gradient(135deg, #3498DB, #5DADE2);
  }
}

.option-text {
  flex: 1;
  text-align: right;

  .option-title {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
  }

  .option-desc {
    display: block;
    font-size: 13px;
    color: #8E8E93;
    font-weight: 400;
  }
}

.option-arrow {
  color: #C4C4C4;
  font-size: 16px;
  transition: color 0.2s ease;
}

.modal-option:hover .option-arrow {
  color:$green;
}
</style>